import { supabase } from './supabase'

export async function scanTarget(targetType, targetValue, userContext) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 60000)

  try {
    const { data, error } = await supabase.functions.invoke('scan', {
      body: {
        target_type: targetType,
        target_value: targetValue,
        user_context: userContext
      },
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    if (error) {
      console.error('Supabase function error:', error)
      // Extract the actual error body from FunctionsHttpError
      let message = 'Scan failed'
      let body = {}
      try {
        if (error.context) {
          body = await error.context.json()
          message = body.error || message
        }
      } catch {
        message = error.message || message
      }

      const err = new Error(message)
      if (body.rateLimited) err.rateLimited = true
      if (body.insufficientCredits) { err.insufficientCredits = true; err.credits = body.credits }
      if (body.upgradeRequired) err.upgradeRequired = true
      if (body.isLocked) err.isLocked = true
      throw err
    }

    return data

  } catch (error) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      throw new Error('Scan timed out after 60 seconds. Please try again — it should be faster now.')
    }
    console.error('Scan API error:', error)
    throw error
  }
}

export async function getScanHistory() {
  const { data, error } = await supabase
    .from('scans')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return data
}

export async function createCheckoutSession(priceId, planType) {
  const { data, error } = await supabase.functions.invoke('stripe-checkout', {
    body: { priceId, planType }
  })

  if (error) {
    console.error('Checkout error object:', error)
    // Try to extract useful message
    let message = error.message || 'Failed to create checkout session'
    try {
      if (error && typeof error === 'object' && 'context' in error) {
        // sometimes supabase returns the Response object in context
        const body = await error.context.text()
        console.error('Edge Function Error Body:', body)
        try {
          const json = JSON.parse(body)
          if (json.error) message = json.error
        } catch (e) {
          message += ` (${body})`
        }
      }
    } catch (e) {
      console.warn('Could not read error body', e)
    }

    throw new Error(message)
  }

  return data
}

export async function purchaseCredits(quantity) {
  const { data, error } = await supabase.functions.invoke('credit-purchase', {
    body: { quantity }
  })

  if (error) {
    console.error('Credit purchase error:', error)
    throw new Error(error.message || 'Failed to create credit purchase session')
  }

  return data
}

export async function getCreditHistory(limit = 50) {
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) throw error
  return data
}

export async function createPortalSession() {
  const { data, error } = await supabase.functions.invoke('stripe-portal', {
    body: {}
  })

  if (error) {
    console.error('Portal error:', error)
    throw new Error(error.message || 'Failed to create billing portal session')
  }

  return data
}

export async function cancelSubscription() {
  const { data, error } = await supabase.functions.invoke('stripe-cancel', {
    body: {}
  })

  if (error) {
    console.error('Cancel error:', error)
    throw new Error(error.message || 'Failed to cancel subscription')
  }

  return data
}
